import type {
  HeadlineResponse,
  OrganizationRankingResponseRankingResponseRelatedPagedData,
  OrgCompositeRankingResRelatedRankingResponseRelatedPagedData,
  PersonSportRankingResponseRelatedRankingResponseRelatedPagedData,
  PlayerItemResponsePagedData,
  RpmPickListPredictionAccuraciesRelatedPagedData,
  SiteResponse,
  TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { MobileMPU } from 'components/Ads/MobileMPU';
import { MPU } from 'components/Ads/MPU';
import { TeamModule } from 'components/Db/Hub/Modules/TeamModule';
import { CoverList } from 'components/Headlines/ArticleCover/CoverList';
import { FeedList } from 'components/Headlines/ArticleFeed/FeedList';
import { TopRankings as HighSchoolRankings } from 'components/HighSchool/Modules/TopRankings/TopRankings';
import { SEOHomeText } from 'components/Home/SEOHomeText';
import { HomeLayout } from 'components/Layouts/HomeLayout';
import { SignUpFooter } from 'components/Newsletter/SignUpFooter';
import { SignUpModule } from 'components/Newsletter/SignUpModule';
import { PredictionsModule } from 'components/Player/PredictionsModule';
import { PlayerRankingsModule } from 'components/Player/RankingsModule';
import { Connect } from 'components/Social/Connect';
import { FanSiteList } from 'components/Teams/FanSiteList';
import { LatestCommits } from 'components/Teams/LatestCommits';
import { TopHeadlines } from 'components/TopHeadlines';
import { rivalsData } from 'store/mocks/Connect.mock';
import { getArticleLists } from 'utils/articles';

import styles from './RivalsHome.module.scss';

export interface IRivalsPageProps {
  featuredArticles: HeadlineResponse[] | null;
  footballRecruitingArticles: HeadlineResponse[] | null;
  basketballRecruitingArticles: HeadlineResponse[] | null;
  latestCommits: PlayerItemResponsePagedData | null;
  highSchoolArticles: HeadlineResponse[] | null;
  highSchoolRankings: OrgCompositeRankingResRelatedRankingResponseRelatedPagedData | null;
  trendingList: TrendingListItemResponseTrendingListResponseRelatedPagedData | null;
  fanSites: SiteResponse[] | null;
  playerRankings: PersonSportRankingResponseRelatedRankingResponseRelatedPagedData | null;
  latestPredictions: RpmPickListPredictionAccuraciesRelatedPagedData | null;
  teamRecruitingRankings: OrganizationRankingResponseRankingResponseRelatedPagedData | null;
}

export const RivalsHome = ({
  featuredArticles,
  footballRecruitingArticles,
  basketballRecruitingArticles,
  highSchoolArticles,
  trendingList,
  latestCommits,
  highSchoolRankings,
  fanSites,
  playerRankings,
  latestPredictions,
  teamRecruitingRankings,
}: IRivalsPageProps) => {
  const teamSites = fanSites?.filter((site) => site.live && site.inIndex);

  const [
    coverArticlesList,
    footballRecruitingArticlesList,
    basketballRecruitingArticlesList,
    highSchoolArticlesList,
  ] = getArticleLists({
    featuredArticles: {
      list: featuredArticles ?? [],
      sliceLimit: 3,
    },
    lists: [
      {
        list: footballRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: basketballRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: highSchoolArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
    ],
  });

  const latestCommitsList = latestCommits?.list ?? [];
  const showPredictions = !!latestPredictions?.list?.length;

  return (
    <div className={styles.layout}>
      <SEOHomeText className={styles.headline}>
        {/* TODO: Rivals - change to rivals logo */}
        <ServiceLogo type="on3" />
        <h1>
          Delivering trusted High School sports news, analysis, data, and
          insights to fans, athletes, schools, and brands
        </h1>
      </SEOHomeText>
      <HomeLayout>
        <div className={styles.center}>
          <div className={styles.inner}>
            {!!coverArticlesList.length && (
              <div className={styles.coverArticles}>
                <CoverList
                  authorColor="#6F747B"
                  backgroundColor="transparent"
                  categoryColor="var(--site-link-color)"
                  isHome
                  list={coverArticlesList}
                  titleColor="#1D252C"
                />
              </div>
            )}

            {!!trendingList && (
              <div className={styles.trending}>
                <TopHeadlines trendingList={trendingList} />
                {/* TODO: Rivals - change to rivals top 10 */}
                <SignUpModule title="Get the Daily On3 Newsletter in your inbox every morning" />
              </div>
            )}

            <MobileMPU id="mobile_mpu_top" incontent />

            <div className={styles.articleLists}>
              {!!footballRecruitingArticlesList.length && (
                <FeedList
                  list={footballRecruitingArticlesList}
                  section="Football Recruiting"
                  viewAllLinks={[
                    {
                      url: '/rivals/category/football-recruiting/news/',
                      text: 'View All Football Recruiting',
                      title: 'Football Recruiting News',
                    },
                  ]}
                />
              )}

              {!!basketballRecruitingArticlesList.length && (
                <FeedList
                  list={basketballRecruitingArticlesList}
                  section="Basketball Recruiting"
                  viewAllLinks={[
                    {
                      url: '/rivals/category/basketball-recruiting/news/',
                      text: 'View All Basketball Recruiting',
                      title: 'Basketball Recruiting News',
                    },
                  ]}
                />
              )}

              {!!highSchoolArticlesList.length && (
                <FeedList
                  list={highSchoolArticlesList}
                  section="High School"
                  viewAllLinks={[
                    {
                      url: '/high-school/news/',
                      text: 'View All High School News',
                      title: 'High School News',
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </div>

        <aside className={styles.right}>
          <div className={styles.inner}>
            <MPU id="mpu_top" />

            {!!latestCommitsList.length && (
              <LatestCommits list={latestCommitsList} />
            )}

            {!!playerRankings?.list?.length && (
              <PlayerRankingsModule playerRankings={playerRankings} />
            )}

            {showPredictions && (
              <PredictionsModule predictions={latestPredictions} />
            )}

            <MPU id="mpu_middle" />
            <MobileMPU id="mobile_mpu_middle" incontent />
          </div>
        </aside>

        <aside className={styles.left}>
          <div className={styles.inner}>
            {!!trendingList && (
              <div className={styles.trending}>
                <TopHeadlines trendingList={trendingList} />
                {/* TODO: Rivals - change to rivals top 10 */}
                <SignUpModule title="Get the Daily On3 Newsletter in your inbox every morning" />
              </div>
            )}

            {!!teamSites && <FanSiteList list={teamSites} />}
            {!!teamRecruitingRankings && (
              <TeamModule
                selectedSport="football"
                teamRanking={teamRecruitingRankings}
              />
            )}
            {highSchoolRankings && (
              <HighSchoolRankings rankings={highSchoolRankings} />
            )}

            <Connect list={rivalsData} title="On3 Connect" />
          </div>
        </aside>
      </HomeLayout>
      <SignUpFooter />
    </div>
  );
};
